import {
  Avatar,
  StreamMessage,
  useChannelStateContext,
  useChatContext,
} from 'stream-chat-react';

import type { StreamChatType } from '../../types';

type ReadReceiptIndicatorProps = {
  message: StreamMessage<StreamChatType>;
};

type ReaderInfo = {
  id: string;
  name: string;
  image?: string | null;
};

const getMessageDate = (message: StreamMessage<StreamChatType>) => {
  const dateValue = message.updated_at || message.created_at;
  return dateValue ? new Date(dateValue) : null;
};

export const ReadReceiptIndicator = ({
  message,
}: ReadReceiptIndicatorProps) => {
  const { client } = useChatContext<StreamChatType>();
  const { channel } = useChannelStateContext<StreamChatType>();

  const currentUserId = client.user?.id || client.userID;

  if (!channel || !currentUserId) return null;
  if (message.user?.id && message.user?.id !== currentUserId) return null;

  const comparisonDate = getMessageDate(message);
  if (!comparisonDate) return null;

  const readStates = Object.values(channel.state.read || {}).filter(
    ({ user }) => {
      if (!user?.id) return false;
      if (user.id === currentUserId) return false;
      if (message.user?.id && user.id === message.user.id) return false;
      return true;
    }
  );

  if (!readStates.length) return null;

  const readers = readStates.filter(({ last_read }) => {
    if (!last_read) return false;
    return new Date(last_read) >= comparisonDate;
  });

  if (!readers.length) return null;

  const readerInfos = readers.reduce<ReaderInfo[]>((acc, { user }) => {
    const id = user?.id;
    const name = user?.name || user?.id;

    if (!id || !name) {
      return acc;
    }
    if (id === currentUserId) {
      return acc;
    }
    if (message.user?.id && id === message.user.id) {
      return acc;
    }

    acc.push({ id, name, image: user?.image });
    return acc;
  }, []);

  if (!readerInfos.length) return null;

  const maxVisibleReaders = 3;
  const visibleReaders = readerInfos.slice(0, maxVisibleReaders);
  const remainingCount = readerInfos.length - visibleReaders.length;

  return (
    <div className="message-read-indicator" aria-label="Message read by">
      {visibleReaders.map((reader) =>
        reader.image ? (
          <Avatar
            key={reader.id}
            image={reader.image}
            name={reader.name}
            size={18}
          />
        ) : (
          <span
            key={reader.id}
            className="message-read-indicator__name"
            title={reader.name}
          >
            {reader.name}
          </span>
        )
      )}
      {remainingCount > 0 && (
        <span className="message-read-indicator__more">+{remainingCount}</span>
      )}
    </div>
  );
};
